import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getTextByLanguage } from "../api/text";
import { getAlergenos } from "../api/alergeno";
import { getTiposDieta } from "../api/tipoDieta";
import { SERVER_URL } from "../api";
import { voteProducto, getProductoVotes, getUserVotes } from "../api/productoVoto";
import { event } from "../utils/gtag";

const MenuContent = ({ menuConfig, eventData, onNavigateToRatings, contenidoIntermedio }) => {
  const { t, i18n } = useTranslation();
  const [selectedAlergenos, setSelectedAlergenos] = useState([]);
  const [selectedTiposDieta, setSelectedTiposDieta] = useState([]);
  const [hideNoAptoMenores, setHideNoAptoMenores] = useState(false); // Cambiado: ahora es "ocultar" en lugar de "mostrar solo"
  const [expandedCategoria, setExpandedCategoria] = useState(null);
  const [alergenosList, setAlergenosList] = useState([]);
  const [tiposDietaList, setTiposDietaList] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [productoVotos, setProductoVotos] = useState({}); // { "0-0": 5, "0-1": 3 }
  const [productosVotados, setProductosVotados] = useState({}); // { "0-0": true }
  const [showTop3, setShowTop3] = useState(false);

  const currentLang = i18n.language || "es";

  // Cargar alérgenos y tipos de dieta desde el backend
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoadingFilters(true);
        const [alergenosRes, tiposDietaRes] = await Promise.all([
          getAlergenos(),
          getTiposDieta(),
        ]);

        if (alergenosRes.status === 200) {
          const alergenos = alergenosRes.data.filter((a) => a.activo !== false);
          setAlergenosList(alergenos);
        }

        if (tiposDietaRes.status === 200) {
          const tiposDieta = tiposDietaRes.data.filter((t) => t.activo !== false);
          setTiposDietaList(tiposDieta);
        }
      } catch (error) {
        console.error("Error al cargar filtros:", error);
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilters();
  }, []);

  // Cargar votos al iniciar
  useEffect(() => {
    if (eventData?._id) {
      // Tracking: visualización del menú
      event({
        action: "ver_menu",
        category: "Eventos",
        label: "MenuContent",
      });

      // Cargar contadores de votos
      getProductoVotes(eventData._id)
        .then(res => {
          if (res.status === 200) {
            setProductoVotos(res.data);
          }
        })
        .catch(err => console.error("Error al cargar votos:", err));

      // Cargar votos del usuario
      getUserVotes(eventData._id)
        .then(res => {
          if (res.status === 200) {
            setProductosVotados(res.data);
          }
        })
        .catch(err => console.error("Error al cargar votos del usuario:", err));
    }
  }, [eventData?._id]);
  
  // Obtener estilos del contenido intermedio
  const estilos = contenidoIntermedio?.estilos || {};
  const containerStyle = {
    backgroundColor: estilos.colorFondo || "#FFF",
    color: estilos.colorTexto || "#333",
    fontFamily: estilos.fuente || "Roboto, sans-serif",
    padding: estilos.espaciado ? `${estilos.espaciado}px` : "0px",
    minHeight: "100vh",
  };
  const primaryColor = estilos.colorPrimario || "#66bc5f";

  // Obtener texto traducido
  const getTranslatedText = (textObj) => {
    if (!textObj) return "";
    if (typeof textObj === "string") return textObj;
    return textObj[currentLang] || textObj["es"] || Object.values(textObj)[0] || "";
  };

  // Obtener texto traducido para alérgenos y tipos de dieta
  const getAlergenoName = (alergeno) => {
    if (!alergeno) return "";
    const nombre = alergeno.nombre || {};
    if (typeof nombre === "string") return nombre;
    return nombre[currentLang] || nombre["es"] || Object.values(nombre)[0] || "";
  };

  const getTipoDietaName = (tipoDieta) => {
    if (!tipoDieta) return "";
    const nombre = tipoDieta.nombre || {};
    if (typeof nombre === "string") return nombre;
    return nombre[currentLang] || nombre["es"] || Object.values(nombre)[0] || "";
  };

  // Obtener alérgenos y tipos de dieta de un producto
  const getProductoAlergenos = (producto) => {
    if (!producto.alergenos || producto.alergenos.length === 0) return [];
    return alergenosList.filter((a) => producto.alergenos.includes(a._id));
  };

  const getProductoTiposDieta = (producto) => {
    if (!producto.tiposDieta || producto.tiposDieta.length === 0) return [];
    return tiposDietaList.filter((t) => producto.tiposDieta.includes(t._id));
  };

  // Filtrar productos según filtros
  const getFilteredProductos = (productos) => {
    if (!productos) return [];
    return productos.filter((producto) => {
      // Filtrar por alérgenos (ocultar productos que tienen alguno de los alérgenos seleccionados)
      if (selectedAlergenos.length > 0) {
        const productoAlergenos = producto.alergenos || [];
        const tieneAlergenoSeleccionado = selectedAlergenos.some((alergenoId) =>
          productoAlergenos.includes(alergenoId)
        );
        if (tieneAlergenoSeleccionado) return false; // Ocultar productos que tienen el alérgeno seleccionado
      }

      // Filtrar por tipos de dieta (mostrar solo productos que tienen al menos uno de los tipos de dieta seleccionados)
      if (selectedTiposDieta.length > 0) {
        const productoTiposDieta = producto.tiposDieta || [];
        const tieneTipoDietaSeleccionado = selectedTiposDieta.some((tipoDietaId) =>
          productoTiposDieta.includes(tipoDietaId)
        );
        if (!tieneTipoDietaSeleccionado) return false;
      }

      // Filtrar por no apto para menores (si el check está marcado, ocultar los no aptos)
      if (hideNoAptoMenores && producto.noAptoMenores) {
        return false;
      }

      // Solo mostrar productos disponibles
      if (producto.disponible === false) return false;

      return true;
    });
  };

  const categorias = menuConfig?.categorias || [];
  const mostrarPrecios = menuConfig?.mostrarPrecios !== false;
  const moneda = menuConfig?.moneda || "EUR";
  const formatoVisualizacion = menuConfig?.formatoVisualizacion || "grid";

  // Obtener todos los alérgenos y tipos de dieta únicos de todos los productos
  const getAllAlergenos = () => {
    const alergenosSet = new Set();
    categorias.forEach((categoria) => {
      (categoria.productos || []).forEach((producto) => {
        (producto.alergenos || []).forEach((alergenoId) => {
          alergenosSet.add(alergenoId);
        });
      });
    });
    return Array.from(alergenosSet);
  };

  const getAllTiposDieta = () => {
    const tiposDietaSet = new Set();
    categorias.forEach((categoria) => {
      (categoria.productos || []).forEach((producto) => {
        (producto.tiposDieta || []).forEach((tipoDietaId) => {
          tiposDietaSet.add(tipoDietaId);
        });
      });
    });
    return Array.from(tiposDietaSet);
  };

  // Obtener alérgenos y tipos de dieta disponibles para filtrar (solo los que están en productos)
  const alergenosDisponibles = alergenosList.filter((a) => getAllAlergenos().includes(a._id));
  const tiposDietaDisponibles = tiposDietaList.filter((t) => getAllTiposDieta().includes(t._id));

  // Función para calcular top 3 productos más votados
  const getTop3Productos = () => {
    const todosProductos = [];
    
    categorias.forEach((categoria, catIndex) => {
      if (categoria.activo === false) return;
      const productosFiltrados = getFilteredProductos(categoria.productos || []);
      productosFiltrados.forEach((producto, prodIndex) => {
        const key = `${catIndex}-${prodIndex}`;
        const votos = productoVotos[key] || 0;
        todosProductos.push({
          producto,
          categoriaIndex: catIndex,
          productoIndex: prodIndex,
          categoria,
          votos,
        });
      });
    });
    
    // Ordenar por votos (descendente) y tomar top 3
    return todosProductos
      .sort((a, b) => b.votos - a.votos)
      .slice(0, 3);
  };

  // Función para votar/desvotar por un producto (toggle)
  const handleVotarProducto = async (categoriaIndex, productoIndex) => {
    if (!eventData?._id) return;
    
    const key = `${categoriaIndex}-${productoIndex}`;
    const yaVotado = productosVotados[key] || false;
    const contadorActual = productoVotos[key] || 0;
    
    // Tracking: voto a producto
    event({
      action: yaVotado ? "desvotar_producto" : "votar_producto",
      category: "Menús",
      label: `Categoría ${categoriaIndex} - Producto ${productoIndex}`,
      value: contadorActual,
    });
    
    // Optimistic update
    setProductosVotados({ ...productosVotados, [key]: !yaVotado });
    setProductoVotos({ 
      ...productoVotos, 
      [key]: yaVotado ? Math.max(0, contadorActual - 1) : contadorActual + 1
    });
    
    try {
      const res = await voteProducto(eventData._id, categoriaIndex, productoIndex);
      if (res.status === 200) {
        setProductoVotos({ 
          ...productoVotos, 
          [key]: res.data.contador 
        });
        setProductosVotados({ ...productosVotados, [key]: res.data.yaVotado });
      }
    } catch (error) {
      console.error("Error al votar:", error);
      // Revertir en caso de error
      setProductosVotados({ ...productosVotados, [key]: yaVotado });
      setProductoVotos({ 
        ...productoVotos, 
        [key]: contadorActual
      });
    }
  };

  const renderProducto = (producto, index, categoriaIndex, skipGridItem = false) => {
    const nombre = getTranslatedText(producto.nombre);
    const descripcion = getTranslatedText(producto.descripcion);
    const precio = producto.precio || 0;
    const productoAlergenos = getProductoAlergenos(producto);
    const productoTiposDieta = getProductoTiposDieta(producto);

    // Determinar si usar formato grid (o Top 3)
    const useGridFormat = formatoVisualizacion === "grid" || showTop3;
    
    if (useGridFormat) {
      // Grid: 3 columnas con imágenes
      const cardContent = (
        <Card 
            style={{ 
              height: "100%", 
              display: "flex", 
              flexDirection: "column",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            {producto.imagen && (
              <CardMedia
                component="img"
                height="200"
                image={producto.imagen.startsWith('http') ? producto.imagen : `${SERVER_URL}/${producto.imagen}`}
                alt={nombre}
                style={{ objectFit: "cover" }}
              />
            )}
            <CardContent style={{ 
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              padding: "16px",
            }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                style={{ 
                  color: estilos.colorTexto || "#333",
                  marginBottom: "8px",
                }}
              >
                {nombre}
              </Typography>
              {descripcion && (
                <Typography 
                  variant="body2" 
                  paragraph
                  style={{ 
                    color: estilos.colorTexto ? "rgba(0, 0, 0, 0.7)" : "#666",
                    marginBottom: "12px",
                    flexGrow: 1,
                  }}
                >
                  {descripcion}
                </Typography>
              )}
              
              {/* Alérgenos y tipos de dieta */}
              {(productoAlergenos.length > 0 || productoTiposDieta.length > 0 || producto.noAptoMenores) && (
                <Box style={{ marginBottom: "12px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {productoAlergenos.map((alergeno) => {
                    const iconUrl = alergeno.icono && alergeno.icono !== ""
                      ? (alergeno.icono.startsWith('http') 
                          ? alergeno.icono 
                          : `${SERVER_URL}/${alergeno.icono}`)
                      : null;
                    
                    return iconUrl ? (
                      <img
                        key={alergeno._id}
                        src={iconUrl}
                        alt={getAlergenoName(alergeno)}
                        title={getAlergenoName(alergeno)}
                        style={{ 
                          maxWidth: "32px", 
                          maxHeight: "32px", 
                          objectFit: "contain",
                          borderRadius: "4px",
                          cursor: "pointer"
                        }}
                      />
                    ) : (
                      <Chip
                        key={alergeno._id}
                        label={getAlergenoName(alergeno)}
                        size="small"
                        color="error"
                        variant="outlined"
                        style={{ fontSize: "0.7rem" }}
                      />
                    );
                  })}
                  {productoTiposDieta.map((tipoDieta) => (
                    <Chip
                      key={tipoDieta._id}
                      label={getTipoDietaName(tipoDieta)}
                      size="small"
                      color="success"
                      variant="outlined"
                      style={{ fontSize: "0.7rem" }}
                    />
                  ))}
                  {producto.noAptoMenores && (
                    <Chip 
                      label="18+" 
                      size="small" 
                      color="warning" 
                      style={{ 
                        fontWeight: "bold",
                      }} 
                    />
                  )}
                </Box>
              )}

              {/* Botón de voto (me gusta) */}
              {eventData?._id && (
                <Box style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "10px",
                  marginTop: "12px",
                  marginBottom: "8px"
                }}>
                  <IconButton
                    onClick={() => handleVotarProducto(categoriaIndex, index)}
                    style={{ 
                      color: productosVotados[`${categoriaIndex}-${index}`] ? primaryColor : "#ccc",
                      padding: "8px",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: productosVotados[`${categoriaIndex}-${index}`] ? "scale(1)" : "scale(0.95)",
                    }}
                    sx={{
                      "&:hover": {
                        backgroundColor: productosVotados[`${categoriaIndex}-${index}`] 
                          ? `${primaryColor}15` 
                          : "rgba(0, 0, 0, 0.04)",
                        transform: "scale(1.1)",
                      },
                      "&:active": {
                        transform: "scale(0.9)",
                      },
                      "@keyframes pulse": {
                        "0%": {
                          transform: "scale(1)",
                        },
                        "50%": {
                          transform: "scale(1.2)",
                        },
                        "100%": {
                          transform: "scale(1)",
                        },
                      },
                      "& .MuiSvgIcon-root": {
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        fontSize: "28px",
                        animation: productosVotados[`${categoriaIndex}-${index}`] 
                          ? "pulse 0.5s ease-in-out" 
                          : "none",
                      },
                    }}
                  >
                    {productosVotados[`${categoriaIndex}-${index}`] ? (
                      <FavoriteIcon />
                    ) : (
                      <FavoriteBorderIcon />
                    )}
                  </IconButton>
                  <Typography 
                    variant="body2" 
                    style={{ 
                      color: estilos.colorTexto || "#666", 
                      fontSize: "1rem",
                      fontWeight: "500",
                      minWidth: "20px",
                      transition: "all 0.3s ease",
                      opacity: (productoVotos[`${categoriaIndex}-${index}`] || 0) > 0 ? 1 : 0.5
                    }}
                  >
                    {productoVotos[`${categoriaIndex}-${index}`] || 0}
                  </Typography>
                </Box>
              )}

              <Box style={{ 
                textAlign: "right", 
                marginTop: "auto",
              }}>
                {mostrarPrecios && precio > 0 && (
                  <Typography 
                    variant="h6" 
                    style={{ 
                      color: primaryColor,
                      fontWeight: "bold",
                    }}
                  >
                    {precio.toFixed(2)} {moneda}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
      );

      // Si estamos en Top 3, no envolver en Grid item
      if (showTop3) {
        return cardContent;
      }

      // Si es grid normal, envolver en Grid item
      return (
        <Grid item xs={12} sm={6} md={4} key={index}>
          {cardContent}
        </Grid>
      );
    } else {
      // tarjetas: 1 columna completa, más espacioso, sin imágenes
      return (
        <Grid item xs={12} key={index}>
          <Card style={{ 
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            marginBottom: "16px",
          }}>
            <CardContent style={{ padding: "24px" }}>
              <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "20px" }}>
                <Box style={{ flex: 1 }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom 
                    style={{ 
                      color: estilos.colorTexto || "#333",
                      fontWeight: "600",
                      marginBottom: "12px",
                    }}
                  >
                    {nombre}
                  </Typography>
                  {descripcion && (
                    <Typography 
                      variant="body1" 
                      paragraph
                      style={{ 
                        color: estilos.colorTexto ? "rgba(0, 0, 0, 0.7)" : "#666",
                        marginBottom: "16px",
                        lineHeight: "1.6",
                      }}
                    >
                      {descripcion}
                    </Typography>
                  )}
                  {/* Alérgenos y tipos de dieta */}
                  {(productoAlergenos.length > 0 || productoTiposDieta.length > 0) && (
                    <Box style={{ marginBottom: "16px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {productoAlergenos.map((alergeno) => {
                        const iconUrl = alergeno.icono && alergeno.icono !== ""
                          ? (alergeno.icono.startsWith('http') 
                              ? alergeno.icono 
                              : `${SERVER_URL}/${alergeno.icono}`)
                          : null;
                        
                        return iconUrl ? (
                          <img
                            key={alergeno._id}
                            src={iconUrl}
                            alt={getAlergenoName(alergeno)}
                            title={getAlergenoName(alergeno)}
                            style={{ 
                              maxWidth: "32px", 
                              maxHeight: "32px", 
                              objectFit: "contain",
                              borderRadius: "4px",
                              cursor: "pointer"
                            }}
                          />
                        ) : (
                          <Chip
                            key={alergeno._id}
                            label={getAlergenoName(alergeno)}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        );
                      })}
                      {productoTiposDieta.map((tipoDieta) => (
                        <Chip
                          key={tipoDieta._id}
                          label={getTipoDietaName(tipoDieta)}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))}
                      {producto.noAptoMenores && (
                        <Chip 
                          label="18+" 
                          size="small" 
                          color="warning" 
                          style={{ fontWeight: "bold" }} 
                        />
                      )}
                    </Box>
                  )}

                  {/* Botón de voto (me gusta) */}
                  {eventData?._id && (
                    <Box style={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: "10px",
                      marginTop: "12px",
                      marginBottom: "8px"
                    }}>
                      <IconButton
                        onClick={() => handleVotarProducto(categoriaIndex, index)}
                        style={{ 
                          color: productosVotados[`${categoriaIndex}-${index}`] ? primaryColor : "#ccc",
                          padding: "8px",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          transform: productosVotados[`${categoriaIndex}-${index}`] ? "scale(1)" : "scale(0.95)",
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: productosVotados[`${categoriaIndex}-${index}`] 
                              ? `${primaryColor}15` 
                              : "rgba(0, 0, 0, 0.04)",
                            transform: "scale(1.1)",
                          },
                          "&:active": {
                            transform: "scale(0.9)",
                          },
                          "@keyframes pulse": {
                            "0%": {
                              transform: "scale(1)",
                            },
                            "50%": {
                              transform: "scale(1.2)",
                            },
                            "100%": {
                              transform: "scale(1)",
                            },
                          },
                          "& .MuiSvgIcon-root": {
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            fontSize: "28px",
                            animation: productosVotados[`${categoriaIndex}-${index}`] 
                              ? "pulse 0.5s ease-in-out" 
                              : "none",
                          },
                        }}
                      >
                        {productosVotados[`${categoriaIndex}-${index}`] ? (
                          <FavoriteIcon />
                        ) : (
                          <FavoriteBorderIcon />
                        )}
                      </IconButton>
                      <Typography 
                        variant="body2" 
                        style={{ 
                          color: estilos.colorTexto || "#666", 
                          fontSize: "1rem",
                          fontWeight: "500",
                          minWidth: "20px",
                          transition: "all 0.3s ease",
                          opacity: (productoVotos[`${categoriaIndex}-${index}`] || 0) > 0 ? 1 : 0.5
                        }}
                      >
                        {productoVotos[`${categoriaIndex}-${index}`] || 0}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "12px", minWidth: "120px" }}>
                  {mostrarPrecios && precio > 0 && (
                    <Typography 
                      variant="h5" 
                      style={{ 
                        color: primaryColor,
                        fontWeight: "bold",
                      }}
                    >
                      {precio.toFixed(2)} {moneda}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      );
    }
  };

  // Obtener configuración del botón
  const botonRatings = contenidoIntermedio?.botonRatings || {};
  const posicionBoton = botonRatings.posicion || "abajo";

  // Estado para forzar la recalculación de la frase aleatoria
  const [fraseRefreshKey, setFraseRefreshKey] = useState(0);
  
  // Estado para mostrar/ocultar el texto del header
  // Primera vez: mostrar completo, siguientes veces: mostrar solo primera frase
  const [mostrarTextoHeader, setMostrarTextoHeader] = useState(true); // Por defecto mostrar completo
  
  // Inicializar el estado según si ya se ha visitado antes
  useEffect(() => {
    if (eventData?._id) {
      const storageKey = `headerTextExpanded_${eventData._id}`;
      const hasBeenVisited = localStorage.getItem(storageKey);
      // Primera vez = true (completo), siguientes = false (colapsado)
      setMostrarTextoHeader(!hasBeenVisited);
    }
  }, [eventData?._id]);

  // Seleccionar frase aleatoria de forma estable (solo cambia cuando cambian las frases, el idioma o el refreshKey)
  const fraseSeleccionada = useMemo(() => {
    if (!eventData?.frasesSabiasQue || !Array.isArray(eventData.frasesSabiasQue) || eventData.frasesSabiasQue.length === 0) {
      return null;
    }
    
    const currentLang = i18n.language?.split('-')[0]?.toLowerCase() || 'es';
    
    // Seleccionar una frase aleatoria (se ejecuta cuando cambian las frases, el idioma o se fuerza refresh)
    const randomIndex = Math.floor(Math.random() * eventData.frasesSabiasQue.length);
    const fraseObj = eventData.frasesSabiasQue[randomIndex];
    
    // Obtener la frase en el idioma actual
    let frase = '';
    if (typeof fraseObj === 'string') {
      frase = fraseObj;
    } else if (typeof fraseObj === 'object' && fraseObj !== null) {
      frase = fraseObj[currentLang] || fraseObj['es'] || Object.values(fraseObj)[0] || '';
    }
    
    return frase && frase.trim() !== '' ? frase : null;
  }, [eventData?.frasesSabiasQue, i18n.language, fraseRefreshKey]);

  // Verificar si hay más de una frase disponible para mostrar el botón de refresh
  const tieneMultiplesFrases = eventData?.frasesSabiasQue && Array.isArray(eventData.frasesSabiasQue) && eventData.frasesSabiasQue.length > 1;

  return (
    <Container maxWidth="lg" style={{ ...containerStyle }}>
      {/* Botón arriba si está configurado así */}
      {posicionBoton === "arriba" && (
        <Box style={{ marginBottom: "30px", textAlign: "center" }}>
          {(() => {
            const estilo = botonRatings.estilo || {};
            const textoBoton = getTranslatedText(botonRatings.texto) || t("event.menu.rateEvent");
            const icono = estilo.icono || "";
            
            const getButtonSize = () => {
              switch (estilo.tamano) {
                case "pequeno":
                  return { padding: "6px 14px", fontSize: "14px", width: "200px" };
                case "grande":
                  return { padding: "10px 50px", fontSize: "20px", width: "300px" };
                case "muy-grande":
                  return { padding: "10px 60px", fontSize: "22px", width: "350px" };
                default:
                  return { padding: "10px 40px", fontSize: "18px", width: "260px" };
              }
            };

            const buttonSize = getButtonSize();
            return (
              <Button
                variant="contained"
                onClick={onNavigateToRatings}
                style={{
                  ...buttonSize,
                  backgroundColor: estilo.colorFondo || "#66bc5f",
                  color: estilo.colorTexto || "white",
                  border: "none",
                  borderRadius: "30px",
                  cursor: "pointer",
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  gap: "4px"
                }}
              >
                {textoBoton}
              </Button>
            );
          })()}
        </Box>
      )}

      {/* Header del menú */}
      <Box style={{ 
        marginBottom: "20px", 
        textAlign: "center",
        padding: "20px",
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          style={{ 
            color: estilos.colorTexto || "#333",
            fontWeight: "700",
            marginBottom: "12px",
          }}
        >
          {getTranslatedText(eventData?.nombre) || "Menú"}
        </Typography>
        {eventData?.descripcion && (
          <Typography 
            variant="body1" 
            style={{ 
              color: estilos.colorTexto ? "rgba(0, 0, 0, 0.7)" : "#666",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            {getTranslatedText(eventData.descripcion)}
          </Typography>
        )}
      </Box>


      {/* Frases "¿Sabías que..." */}
      {fraseSeleccionada && (
        <Box style={{
          marginBottom: "20px",
          padding: "16px 20px",
          backgroundColor: estilos.colorPrimario ? `${estilos.colorPrimario}15` : "#e8f5e9",
          borderRadius: "12px",
          borderLeft: `4px solid ${estilos.colorPrimario || "#66bc5f"}`,
          textAlign: "left",
          position: "relative",
        }}>
          <Typography 
            variant="body1" 
            style={{
              color: estilos.colorTexto || "#333",
              fontStyle: "italic",
              fontSize: "0.95rem",
              lineHeight: "1.6",
              paddingRight: tieneMultiplesFrases ? "32px" : "0", // Espacio para el icono solo si hay múltiples frases
            }}
          >
            <strong style={{ color: estilos.colorPrimario || "#66bc5f" }}>
              {t("event.menu.didYouKnow")}
            </strong>{" "}
            {fraseSeleccionada}
          </Typography>
          {tieneMultiplesFrases && (
            <IconButton
              size="small"
              onClick={async () => {
                setFraseRefreshKey(prev => prev + 1);
                // Registrar click en el backend
                if (eventData?._id) {
                  try {
                    const response = await fetch(`${SERVER_URL}/api/event/${eventData._id}/frases-refresh-click`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                    });
                    if (response.ok) {
                      // Tracking: click en botón de recargar frase
                      event({
                        action: "refresh_frase_sabias_que",
                        category: "Eventos",
                        label: "MenuContent",
                      });
                    }
                  } catch (error) {
                    console.error("Error al registrar click en botón de recargar frase:", error);
                  }
                }
              }}
              style={{
                position: "absolute",
                bottom: "8px",
                right: "8px",
                color: estilos.colorPrimario || "#66bc5f",
                padding: "4px",
              }}
              title="Cambiar frase"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* Contenido de Cabecera (si está configurado) */}
      {eventData?.cabeceraContenido && (() => {
        const contenido = eventData.cabeceraContenido;
        const tieneTexto = contenido.texto && typeof contenido.texto === 'object' && Object.values(contenido.texto).some(v => v && String(v).trim() !== '');
        const tieneMedio = contenido.urlMedio && contenido.urlMedio.trim() !== '';
        
        if (!tieneTexto && !tieneMedio) return null;
        
        const textoTraducido = getTranslatedText(contenido.texto);
        
        // Función para obtener la primera frase del texto HTML
        const obtenerPrimeraFrase = (htmlText) => {
          if (!htmlText) return '';
          // Crear un elemento temporal para parsear el HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlText;
          const textContent = tempDiv.textContent || tempDiv.innerText || '';
          
          // Buscar el primer punto, exclamación o interrogación seguido de espacio o fin de texto
          const match = textContent.match(/^[^.!?]*(?:[.!?][\s\n]|$)/);
          if (match && match[0].trim().length > 0) {
            return match[0].trim();
          }
          
          // Si no hay puntuación, tomar los primeros 150 caracteres
          return textContent.length > 40 ? textContent.substring(0, 40).trim() + '...' : textContent.trim();
        };
        
        const primeraFrase = obtenerPrimeraFrase(textoTraducido);
        const handleToggleTexto = () => {
          const nuevoEstado = !mostrarTextoHeader;
          setMostrarTextoHeader(nuevoEstado);
          // Guardar en localStorage que ya se ha visitado
          if (eventData?._id) {
            const storageKey = `headerTextExpanded_${eventData._id}`;
            localStorage.setItem(storageKey, 'visited');
          }
        };
        
        return (
          <Box style={{
            marginBottom: "20px",
            padding: "20px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}>
            {tieneTexto && textoTraducido && (
              <Box style={{ position: "relative" }}>
                {mostrarTextoHeader ? (
                  <>
                    <div
                      dangerouslySetInnerHTML={{ __html: textoTraducido }}
                      style={{
                        color: estilos.colorTexto || "#333",
                        marginBottom: tieneMedio ? "16px" : 0,
                        lineHeight: "1.6",
                        textAlign: "center",
                        fontSize: "1rem",
                      }}
                    />
                    <Box style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      marginTop: "12px",
                    }}>
                      <Box
                        onClick={handleToggleTexto}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          color: estilos.colorPrimario || "#66bc5f",
                          userSelect: "none",
                          marginBottom: "10px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            color: estilos.colorPrimario || "#66bc5f",
                            fontWeight: "500",
                            marginRight: "4px",
                          }}
                        >
                          Ver menos
                        </Typography>
                        <KeyboardArrowUpIcon style={{ fontSize: "20px" }} />
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography
                      style={{
                        color: estilos.colorTexto || "#333",
                        marginBottom: tieneMedio ? "16px" : 0,
                        lineHeight: "1.6",
                        textAlign: "center",
                        fontSize: "1rem",
                      }}
                    >
                      {primeraFrase}
                    </Typography>
                    <Box style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      marginTop: "12px",
                    }}>
                      <Box
                        onClick={handleToggleTexto}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          color: estilos.colorPrimario || "#66bc5f",
                          userSelect: "none",
                          marginBottom: "10px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            color: estilos.colorPrimario || "#66bc5f",
                            fontWeight: "500",
                            marginRight: "4px",
                          }}
                        >
                          Ver más
                        </Typography>
                        <KeyboardArrowDownIcon style={{ fontSize: "20px" }} />
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}
            {tieneMedio && contenido.tipoMedio === "imagen" && (
              <Box style={{ textAlign: "center" }}>
                <img
                  src={contenido.urlMedio.startsWith('http') ? contenido.urlMedio : `${SERVER_URL}/${contenido.urlMedio}`}
                  alt="Contenido de cabecera"
                  style={{ 
                    maxWidth: "100%", 
                    height: "auto",
                    borderRadius: "8px",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </Box>
            )}
            {tieneMedio && contenido.tipoMedio === "video" && (
              <Box style={{ textAlign: "center" }}>
                {contenido.urlMedio.includes("youtube.com") || contenido.urlMedio.includes("youtu.be") ? (
                  <div
                    style={{
                      position: "relative",
                      paddingBottom: "56.25%", // 16:9 aspect ratio
                      height: 0,
                      overflow: "hidden",
                      maxWidth: "100%",
                      borderRadius: "8px",
                    }}
                  >
                    <iframe
                      src={contenido.urlMedio.includes("embed") ? contenido.urlMedio : `https://www.youtube.com/embed/${contenido.urlMedio.split("v=")[1]?.split("&")[0] || contenido.urlMedio.split("youtu.be/")[1]?.split("?")[0]}`}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                        borderRadius: "8px",
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <video
                    src={contenido.urlMedio.startsWith('http') ? contenido.urlMedio : `${process.env.REACT_APP_API_URL || 'https://api.tipify.tips'}/${contenido.urlMedio}`}
                    controls
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "8px",
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        );
      })()}

      {/* Filtros */}
      <Paper style={{ 
        padding: "20px", 
        marginBottom: "20px",
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}>
        <Grid container spacing={3}>
          {/* Filtro de alérgenos */}
          {alergenosDisponibles.length > 0 && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t("event.menu.removeAllergens")}</InputLabel>
                  <Select
                  multiple
                  value={selectedAlergenos}
                  onChange={(e) => {
                    event({
                      action: "filtrar_alergenos",
                      category: "Menús",
                      label: "MenuContent",
                      value: e.target.value.length,
                    });
                    setSelectedAlergenos(e.target.value);
                  }}
                  label={t("event.menu.removeAllergens")}
                  renderValue={(selected) => {
                    if (selected.length === 0) return t("event.menu.none");
                    if (selected.length === 1) {
                      const alergeno = alergenosList.find((a) => a._id === selected[0]);
                      const iconUrl = alergeno?.icono && alergeno.icono !== ""
                        ? (alergeno.icono.startsWith('http') 
                            ? alergeno.icono 
                            : `${SERVER_URL}/${alergeno.icono}`)
                        : null;
                      return (
                        <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {iconUrl && (
                            <img
                              src={iconUrl}
                              alt={getAlergenoName(alergeno)}
                              style={{ 
                                width: "20px", 
                                height: "20px", 
                                objectFit: "contain",
                                borderRadius: "4px"
                              }}
                            />
                          )}
                          <span>{t("event.menu.removeAllergen", { name: getAlergenoName(alergeno) })}</span>
                        </Box>
                      );
                    }
                    return t("event.menu.removeAllergensCount", { count: selected.length });
                  }}
                >
                  {alergenosDisponibles.map((alergeno) => {
                    const iconUrl = alergeno.icono && alergeno.icono !== ""
                      ? (alergeno.icono.startsWith('http') 
                          ? alergeno.icono 
                          : `${SERVER_URL}/${alergeno.icono}`)
                      : null;
                    
                    return (
                      <MenuItem key={alergeno._id} value={alergeno._id}>
                        <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {iconUrl && (
                            <img
                              src={iconUrl}
                              alt={getAlergenoName(alergeno)}
                              style={{ 
                                width: "24px", 
                                height: "24px", 
                                objectFit: "contain",
                                borderRadius: "4px"
                              }}
                            />
                          )}
                          <span>{getAlergenoName(alergeno)}</span>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Filtro de tipos de dieta */}
          {tiposDietaDisponibles.length > 0 && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t("event.menu.filterDiet")}</InputLabel>
                  <Select
                  multiple
                  value={selectedTiposDieta}
                  onChange={(e) => {
                    event({
                      action: "filtrar_tipos_dieta",
                      category: "Menús",
                      label: "MenuContent",
                      value: e.target.value.length,
                    });
                    setSelectedTiposDieta(e.target.value);
                  }}
                  label={t("event.menu.filterDiet")}
                  renderValue={(selected) => {
                    if (selected.length === 0) return t("event.menu.all");
                    if (selected.length === 1) {
                      const tipoDieta = tiposDietaList.find((t) => t._id === selected[0]);
                      return getTipoDietaName(tipoDieta);
                    }
                    return t("event.menu.dietTypesSelected", { count: selected.length });
                  }}
                >
                  {tiposDietaDisponibles.map((tipoDieta) => (
                    <MenuItem key={tipoDieta._id} value={tipoDieta._id}>
                      {getTipoDietaName(tipoDieta)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Switch de no apto para menores */}
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideNoAptoMenores}
                  onChange={(e) => {
                    event({
                      action: "toggle_ocultar_menores",
                      category: "Menús",
                      label: "MenuContent",
                      value: e.target.checked ? 1 : 0,
                    });
                    setHideNoAptoMenores(e.target.checked);
                  }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#66bc5f",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#66bc5f",
                    },
                  }}
                />
              }
              label={t("event.menu.hideMinors")}
              style={{ 
                marginTop: "8px",
                color: estilos.colorTexto || "#333",
              }}
            />
          </Grid>

          {/* Botón Top 3 */}
          {eventData?._id && (
            <Grid item xs={12} style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
              <Button
                variant={showTop3 ? "contained" : "outlined"}
                onClick={() => {
                  event({
                    action: showTop3 ? "ver_todos_platos" : "ver_top3",
                    category: "Menús",
                    label: "MenuContent",
                  });
                  setShowTop3(!showTop3);
                }}
                startIcon={<TrendingUpIcon />}
                style={{
                  backgroundColor: showTop3 ? primaryColor : "transparent",
                  color: showTop3 ? "white" : primaryColor,
                  borderColor: primaryColor,
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderRadius: "25px",
                  padding: "10px 24px",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "16px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: showTop3 ? primaryColor : `${primaryColor}15`,
                    transform: "scale(1.05)",
                  },
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: showTop3 ? primaryColor : `${primaryColor}15`,
                    transform: "scale(1.05)",
                  },
                }}
              >
                {showTop3 ? t("event.menu.top3.buttonShowAll") : t("event.menu.top3.button")}
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Categorías y productos */}
      {categorias.length === 0 ? (
        <Paper style={{ 
          padding: "40px", 
          textAlign: "center",
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}>
          <Typography 
            variant="body1" 
            style={{ 
              color: estilos.colorTexto ? "rgba(0, 0, 0, 0.6)" : "#666",
            }}
          >
            {t("event.menu.noCategories")}
          </Typography>
        </Paper>
      ) : showTop3 ? (
        // Vista Top 3
        <Box>
          <Paper style={{ 
            padding: "24px", 
            marginBottom: "24px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}>
            <Typography 
              variant="h5" 
              style={{ 
                color: estilos.colorTexto || "#333",
                fontWeight: "700",
                marginBottom: "8px",
              }}
            >
              {t("event.menu.top3.title")}
            </Typography>
            <Typography 
              variant="body2" 
              style={{ 
                color: estilos.colorTexto ? "rgba(0, 0, 0, 0.6)" : "#666",
              }}
            >
              {t("event.menu.top3.subtitle")}
            </Typography>
          </Paper>
          <Grid container spacing={4} justifyContent="center">
            {getTop3Productos().map((item, index) => {
              const posicionesColores = [
                "#FFD700", // Oro para #1
                "#C0C0C0", // Plata para #2
                "#CD7F32", // Bronce para #3
              ];
              const posicionTexto = ["🥇", "🥈", "🥉"];
              return (
                <Grid item xs={12} sm={10} md={4} key={`top-${index}`} style={{ display: "flex", justifyContent: "center" }}>
                  <Box style={{ position: "relative", width: "100%", maxWidth: "400px" }}>
                    {/* Badge de posición - arriba centrado en desktop, esquina dentro en mobile */}
                    <Box
                      sx={{
                        position: "absolute",
                        // Mobile: dentro de la card, esquina superior derecha
                        // Desktop: arriba centrado
                        top: { xs: "12px", md: "-20px" },
                        right: { xs: "12px", md: "auto" },
                        left: { xs: "auto", md: "50%" },
                        transform: { xs: "none", md: "translateX(-50%)" },
                        zIndex: 10,
                        backgroundColor: posicionesColores[index] || primaryColor,
                        color: "white",
                        borderRadius: "50%",
                        width: { xs: "40px", md: "60px" },
                        height: { xs: "40px", md: "60px" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: { xs: "18px", md: "28px" },
                        fontWeight: "bold",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                        border: { xs: "2px solid white", md: "4px solid white" },
                        fontFamily: "Arial, sans-serif",
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ paddingTop: { xs: "0px", md: "35px" } }}>
                      {renderProducto(item.producto, item.productoIndex, item.categoriaIndex)}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
            {getTop3Productos().length === 0 && (
              <Grid item xs={12}>
                <Paper style={{ 
                  padding: "40px", 
                  textAlign: "center",
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}>
                  <Typography 
                    variant="body1" 
                    style={{ 
                      color: estilos.colorTexto ? "rgba(0, 0, 0, 0.6)" : "#666",
                    }}
                  >
                    {t("event.menu.top3.noVotes")}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      ) : (
        categorias
          .filter((categoria) => categoria.activo !== false)
          .map((categoria, catIndex) => {
            const categoriaNombre = getTranslatedText(categoria.nombre);
            const categoriaDescripcion = getTranslatedText(categoria.descripcion);
            const productosFiltrados = getFilteredProductos(categoria.productos || []);

            if (productosFiltrados.length === 0) return null;

            return (
              <Accordion
                key={catIndex}
                expanded={expandedCategoria === catIndex}
                onChange={() => setExpandedCategoria(expandedCategoria === catIndex ? null : catIndex)}
                sx={{ 
                  marginBottom: "8px",
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  "&:before": { display: "none"},
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon style={{ color: primaryColor }} />}
                  style={{ 
                    padding: "12px 14px",
                  }}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      margin: 0,
                      flex: 1
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      marginLeft: "auto",
                      flexGrow: 0,
                    },
                  }}
                >
                  <Box style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                    {categoria.imagen && (
                      <img
                        src={categoria.imagen.startsWith('http') ? categoria.imagen : `${SERVER_URL}/${categoria.imagen}`}
                        alt={categoriaNombre}
                        style={{ 
                          width: "60px", 
                          height: "60px", 
                          objectFit: "cover", 
                          borderRadius: "8px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography 
                      variant="h5" 
                      style={{ 
                        color: estilos.colorTexto || "#333",
                      }}
                    >
                      {categoriaNombre || `Categoría ${catIndex + 1}`}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails style={{ padding: "12px" }}>
                  {categoriaDescripcion && (
                    <Typography 
                      variant="body2" 
                      paragraph
                      style={{ 
                        color: estilos.colorTexto ? "rgba(0, 0, 0, 0.7)" : "#666",
                        marginBottom: "20px",
                      }}
                    >
                      {categoriaDescripcion}
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    {productosFiltrados.map((producto, prodIndex) =>
                      renderProducto(producto, prodIndex, catIndex)
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
      )}

      {/* Botón para ir a ratings */}
      {posicionBoton !== "arriba" && (
        <Box style={{ marginTop: "40px", textAlign: "center" }}>
          {(() => {
            const estilo = botonRatings.estilo || {};
            const textoBoton = getTranslatedText(botonRatings.texto) || t("event.menu.rateEvent");
            const icono = estilo.icono || "";
            
            const getButtonSize = () => {
              switch (estilo.tamano) {
                case "pequeno":
                  return { padding: "6px 14px", fontSize: "14px", width: "200px" };
                case "grande":
                  return { padding: "10px 50px", fontSize: "20px", width: "300px" };
                case "muy-grande":
                  return { padding: "10px 60px", fontSize: "22px", width: "350px" };
                default:
                  return { padding: "10px 40px", fontSize: "18px", width: "260px" };
              }
            };

            const buttonSize = getButtonSize();
            const buttonStyle = {
              ...buttonSize,
              backgroundColor: estilo.colorFondo || "#66bc5f",
              color: estilo.colorTexto || "white",
              border: "none",
              borderRadius: "30px",
              cursor: "pointer",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              gap: "4px"
            };

            // Si es flotante, aplicar estilos especiales
            if (posicionBoton === "flotante") {
              return (
                <Box
                  style={{
                    position: "fixed",
                    bottom: "calc(20px + var(--cookie-bar-h, 0px))",
                    left: "20px",
                    zIndex: 1000,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={onNavigateToRatings}
                    style={buttonStyle}
                  >
                    {textoBoton}
                  </Button>
                </Box>
              );
            }

            return (
              <Button
                variant="contained"
                onClick={onNavigateToRatings}
                style={buttonStyle}
              >
                {textoBoton}
              </Button>
            );
          })()}
        </Box>
      )}
    </Container>
  );
};

export default MenuContent;
